import { toast } from 'sonner';
import { translate, type MessageDef } from '@/shared/i18n/define';
import type { AppStoreLike } from '@/shared/store/listeners/types';
import { detachFromServer } from '@/shared/store/slices/prefsSlice';
import { logout, setSession } from '@/shared/store/slices/authSlice';
import { setApiReachable } from '@/shared/store/slices/uiSlice';
import { configureApiBridge, type ApiBridge } from './bridge';
import { authApi } from './services/authApi';
import { bridgeMsgs } from './authBridgeMsgs';
import { httpClient } from './client';

/**
 * Conecta la capa HTTP con el store (docs/ARQUITECTURA.md §11):
 *  - el Bearer sale del store (el access token vive solo en memoria),
 *  - ante un 401 se renueva la sesión UNA vez (single-flight) y se reintenta; si el refresh es inválido o
 *    reutilizado se cierra la sesión SIN redirigir (la app anónima es un modo válido),
 *  - un fallo de RED al renovar no cierra la sesión (puede que solo no haya internet),
 *  - avisos discretos: sesión vencida y errores 5xx (máximo uno cada 30 s).
 */
export const AVISO_SERVIDOR_MS = 30_000;

export type ResultadoRefresh = 'ok' | 'sin_token' | 'invalida' | 'red';

/** Modo cookie (el refresh viaja en cookie httpOnly): lo informa `/v1/capacidades`. */
export function applyCookieMode(activo: boolean): void {
  httpClient.defaults.withCredentials = activo;
}

const cookieModeGuardado = (store: AppStoreLike): boolean =>
  store.getState().mercado.capacidades?.data.auth.cookieMode ?? false;

let enCurso: Promise<ResultadoRefresh> | null = null;
let ultimoResultado: ResultadoRefresh = 'ok';

const aviso = (store: AppStoreLike, clave: 'sesionVencida' | 'servidorFalla', id: string): void => {
  const texto = translate(
    bridgeMsgs as unknown as MessageDef,
    store.getState().lang.current,
    clave,
  );
  if (clave === 'sesionVencida') toast(texto, { id });
  else toast.error(texto, { id });
};

/** Renueva la sesión con el refresh token del store (o la cookie). Una sola petición a la vez. */
export function refrescarSesion(store: AppStoreLike): Promise<ResultadoRefresh> {
  enCurso ??= (async (): Promise<ResultadoRefresh> => {
    const token = store.getState().auth.refreshToken;
    if (!token && !cookieModeGuardado(store)) return 'sin_token';
    const r = await authApi.refresh(token);
    if (r.ok) {
      store.dispatch(setSession({ user: r.data.usuario, tokens: r.data.tokens }));
      return 'ok';
    }
    return r.status === 0 ? 'red' : 'invalida';
  })()
    .then((resultado) => (ultimoResultado = resultado))
    .finally(() => {
      enCurso = null;
    });
  return enCurso;
}

/** Cierra la sesión local (sin tocar el servidor) y corta el vínculo de las preferencias. */
export function cerrarSesionLocal(store: AppStoreLike, conAviso: boolean): void {
  store.dispatch(logout());
  store.dispatch(detachFromServer());
  if (conAviso) aviso(store, 'sesionVencida', 'sesion-vencida');
}

export function crearPuenteApi(store: AppStoreLike): ApiBridge {
  let ultimoAvisoServidor = 0;
  return {
    getAccessToken: () => store.getState().auth.accessToken,
    refreshSession: async () => {
      const resultado = await refrescarSesion(store);
      return resultado === 'ok' ? store.getState().auth.accessToken : null;
    },
    onSessionLost: () => {
      // Sin internet no se sabe si la sesión sigue viva: se conserva.
      if (ultimoResultado === 'red') return;
      cerrarSesionLocal(store, true);
    },
    onServerError: () => {
      const ahora = Date.now();
      if (ahora - ultimoAvisoServidor < AVISO_SERVIDOR_MS) return;
      ultimoAvisoServidor = ahora;
      aviso(store, 'servidorFalla', 'servidor-falla');
    },
    onReachability: (alcanzable) => {
      if (store.getState().ui.apiReachable !== alcanzable)
        store.dispatch(setApiReachable(alcanzable));
    },
  };
}

/** Se llama una vez al arrancar la app (después de crear el store). */
export function setupApiBridge(store: AppStoreLike): void {
  applyCookieMode(cookieModeGuardado(store));
  configureApiBridge(crearPuenteApi(store));
}
